/**
 * Search date sort
 *
 * Shopify search cannot sort by created_at. Date is a theme-only param (theme_sort).
 * Shopify matching always uses relevance (no sort_by=created-*). Native Relevance and
 * Price sorts leave this module idle.
 *
 * 1. Fetch compact JSON meta pages (250/page, max 8) for the current q + filters
 * 2. Sort the complete set by created_at only
 * 3. Fetch full PLP product cards only for the visible page via ?view=card
 *
 * Caps: META_PAGE_SIZE, MAX_META_PAGES, MAX_PRODUCTS, DEFAULT_TIMEOUT_MS, EXPLICIT_TIMEOUT_MS
 */

const THEME_SORT_PARAM = 'theme_sort';
const META_PAGE_SIZE = 250;
const MAX_META_PAGES = 8;
const MAX_PRODUCTS = 2000;
const META_CONCURRENCY = 3;
const CARD_CONCURRENCY = 8;
/** Default Date (header search): allow enough time for ~900 matches + visible cards. */
const DEFAULT_TIMEOUT_MS = 20000;
/** Idle gap with no progress before treating the run as stuck (default Date only). */
const DEFAULT_IDLE_MS = 8000;
const EXPLICIT_TIMEOUT_MS = 60000;
const EXPLICIT_MODAL_AT_MS = 8000;
const EXPLICIT_STORAGE_KEY = 'searchDateSortExplicit';

/** @type {Map<string, Element>} */
const cardCache = new Map();

/** @type {{ key: string, products: Array<{id: number, created_at: number, handle: string}> } | null} */
let metaCache = null;

function isDateSortValue(value) {
  return value === 'created-descending' || value === 'created-ascending';
}

function urlSearchParamsSafe() {
  try {
    return new URL(window.location.href).searchParams;
  } catch (_error) {
    return null;
  }
}

function urlSortBy() {
  return urlSearchParamsSafe()?.get('sort_by') || null;
}

function urlThemeSort() {
  return urlSearchParamsSafe()?.get(THEME_SORT_PARAM) || null;
}

/** Active search sort: Date from theme_sort (or legacy sort_by), else native sort_by. */
function activeSortFromUrl() {
  const themeSort = urlThemeSort();
  if (isDateSortValue(themeSort)) return themeSort;
  const sortBy = urlSortBy();
  if (isDateSortValue(sortBy)) return sortBy;
  return normalizeSearchSort(sortBy);
}

function isSearchResultsPage() {
  return Boolean(document.querySelector('search-date-sort'));
}

function getRoot() {
  return window.Shopify?.routes?.root || '/';
}

function yieldToMain() {
  if (typeof scheduler !== 'undefined' && typeof scheduler.yield === 'function') {
    return scheduler.yield();
  }
  return new Promise((resolve) => requestAnimationFrame(resolve));
}

function buildMetaUrl(page) {
  const current = new URL(window.location.href);
  const root = getRoot();
  const path = `${root.endsWith('/') ? root : `${root}/`}search`;
  const out = new URL(path, current.origin);

  out.search = '';
  out.searchParams.set('q', current.searchParams.get('q') || '');
  const type = current.searchParams.get('type');
  if (type) out.searchParams.set('type', type);
  out.searchParams.set('view', 'date-sort');
  out.searchParams.set('page', String(page));

  current.searchParams.forEach((value, key) => {
    if (key.startsWith('filter.')) {
      out.searchParams.append(key, value);
    }
  });

  return out;
}

function buildCardUrl(handle) {
  const root = getRoot();
  const path = `${root.endsWith('/') ? root : `${root}/`}products/${encodeURIComponent(handle)}`;
  const out = new URL(path, window.location.origin);
  out.searchParams.set('view', 'card');
  return out.toString();
}

function metaCacheKey() {
  const url = new URL(window.location.href);
  const parts = [`q=${url.searchParams.get('q') || ''}`];
  const type = url.searchParams.get('type');
  if (type) parts.push(`type=${type}`);
  const filters = [];
  url.searchParams.forEach((value, key) => {
    if (key.startsWith('filter.')) {
      filters.push(`${key}=${value}`);
    }
  });
  filters.sort();
  return parts.concat(filters).join('&');
}

function extractJson(text) {
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');
  if (start === -1 || end === -1 || end < start) {
    throw new Error('Invalid meta JSON');
  }
  return JSON.parse(text.slice(start, end + 1));
}

async function mapPool(items, limit, mapper, signal) {
  const results = new Array(items.length);
  let next = 0;

  async function worker() {
    while (next < items.length) {
      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }
      const index = next;
      next += 1;
      results[index] = await mapper(items[index], index);
    }
  }

  const workers = Array.from({ length: Math.min(limit, Math.max(items.length, 1)) }, () => worker());
  await Promise.all(workers);
  return results;
}

function normalizeSearchSort(value) {
  if (isDateSortValue(value)) return value;
  if (value === 'price-ascending' || value === 'price-descending') return value;
  if (value && value !== 'relevance') return value;
  return 'relevance';
}

/** Header / empty-state search starts a new query and must not inherit the page Date. */
function isNewQuerySearchForm(form) {
  return (
    form instanceof HTMLFormElement &&
    (form.id === 'predictive-search-form' ||
      form.classList.contains('header-search__form') ||
      form.classList.contains('main-search-form'))
  );
}

function applyDateToSearchUrl(url, dateSort) {
  if (isDateSortValue(dateSort)) {
    url.searchParams.delete('sort_by');
    url.searchParams.set(THEME_SORT_PARAM, dateSort);
  } else if (dateSort === 'relevance' || !dateSort) {
    url.searchParams.delete('sort_by');
    url.searchParams.delete(THEME_SORT_PARAM);
  } else {
    url.searchParams.set('sort_by', dateSort);
    url.searchParams.delete(THEME_SORT_PARAM);
  }
  return url;
}

function rewriteFacetUrl(url) {
  const sort = url.searchParams.get('sort_by');
  const themeSort = url.searchParams.get(THEME_SORT_PARAM);

  if (isDateSortValue(sort)) {
    url.searchParams.delete('sort_by');
    url.searchParams.set(THEME_SORT_PARAM, sort);
    return;
  }

  if (sort && sort !== 'relevance') {
    url.searchParams.delete(THEME_SORT_PARAM);
    return;
  }

  if (sort === 'relevance') {
    url.searchParams.delete('sort_by');
    url.searchParams.delete(THEME_SORT_PARAM);
    return;
  }

  if (isDateSortValue(themeSort)) {
    url.searchParams.delete('sort_by');
  }
}

function syncSortDropdown(sortValue, { silent = true } = {}) {
  const popover = document.querySelector('#sort-by-popover');
  if (!popover) return;

  const listbox = popover.querySelector('x-listbox');
  let selectedOption = null;

  popover.querySelectorAll('[role="option"]').forEach((option) => {
    if (option.getAttribute('value') === sortValue) {
      option.setAttribute('aria-selected', 'true');
      selectedOption = option;
    } else {
      option.removeAttribute('aria-selected');
    }
  });

  if (!listbox || !selectedOption) return;

  // x-listbox only fires listbox:change when aria-activedescendant changes.
  // Liquid marks Relevance selected when Date is theme-only, then we visually select Date.
  if (!selectedOption.id) {
    selectedOption.id = `option-${crypto.randomUUID ? crypto.randomUUID() : Math.floor(Math.random() * 1e4)}`;
  }

  if (listbox.getAttribute('aria-activedescendant') === selectedOption.id) return;

  if (silent) {
    const blockFacetUpdate = (event) => {
      event.stopImmediatePropagation();
    };
    listbox.addEventListener('listbox:change', blockFacetUpdate, true);
    try {
      listbox.setAttribute('aria-activedescendant', selectedOption.id);
    } finally {
      listbox.removeEventListener('listbox:change', blockFacetUpdate, true);
    }
  } else {
    listbox.setAttribute('aria-activedescendant', selectedOption.id);
  }
}

function syncSortHiddenInputs(sortValue) {
  const isDate = isDateSortValue(sortValue);

  document.querySelectorAll('facets-form input[name="sort_by"]').forEach((input) => {
    if (isDate) {
      input.disabled = true;
      input.value = '';
    } else {
      input.disabled = false;
      input.value = sortValue && sortValue !== 'relevance' ? sortValue : 'relevance';
    }
  });

  document.querySelectorAll('input[name="theme_sort"]').forEach((input) => {
    if (isNewQuerySearchForm(input.closest('form'))) return;
    if (isDate) {
      input.disabled = false;
      input.value = sortValue;
    } else {
      input.disabled = true;
      input.value = '';
    }
  });
}

function preserveSortOnFacetLinks(sortValue) {
  document.querySelectorAll('.collection a[href*="/search"], .active-facets a[href], .facets-clear-all').forEach((anchor) => {
    try {
      const href = new URL(anchor.href, window.location.origin);
      if (!href.pathname.includes('/search')) return;
      applyDateToSearchUrl(href, isDateSortValue(sortValue) ? sortValue : normalizeSearchSort(sortValue));
      if (!isDateSortValue(sortValue) && (!sortValue || sortValue === 'relevance')) {
        href.searchParams.delete(THEME_SORT_PARAM);
        href.searchParams.delete('sort_by');
      }
      anchor.setAttribute('href', href.pathname + href.search + href.hash);
    } catch (_error) {
      // ignore
    }
  });
}

function setPending(isPending) {
  document.documentElement.classList.toggle('search-date-pending', Boolean(isPending));
}

class SearchDateSort extends HTMLElement {
  #abortController = null;
  #generation = 0;
  #sortedProducts = [];
  #applied = false;
  #waitExtended = false;
  #timeoutId = null;
  #modalTimeoutId = null;
  #idleTimeoutId = null;
  #startedAt = 0;
  #modalBound = false;
  #paginationBound = false;

  connectedCallback() {
    if (window.Shopify?.designMode) return;

    this.#releaseScrollLock();

    const sort = activeSortFromUrl();
    syncSortDropdown(sort);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => syncSortDropdown(sort));
    });

    if (!isDateSortValue(sort)) {
      window.__SEARCH_DATE_SORT__ = null;
      setPending(false);
      this.removeAttribute('aria-busy');
      syncSortHiddenInputs(sort);
      return;
    }

    window.__SEARCH_DATE_SORT__ = sort;
    this.setAttribute('sort-direction', sort);
    setPending(true);
    this.setAttribute('aria-busy', 'true');
    syncSortHiddenInputs(sort);
    preserveSortOnFacetLinks(sort);

    this.#abortController = new AbortController();
    this.#generation += 1;
    const generation = this.#generation;
    this.#startedAt = Date.now();
    this.#applied = false;
    this.#waitExtended = false;

    this.#bindModalActions();
    this.#bindPagination();

    this.#run(generation).catch((error) => {
      if (error?.name === 'AbortError' || generation !== this.#generation) return;
      this.#fallbackToRelevance();
    });
  }

  disconnectedCallback() {
    this.#generation += 1;
    this.#clearTimers();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#releaseScrollLock();
  }

  get sectionId() {
    return this.getAttribute('section-id') || '';
  }

  get productsPerPage() {
    const value = Number(this.getAttribute('products-per-page'));
    return Number.isFinite(value) && value > 0 ? value : 24;
  }

  get sortDirection() {
    return this.getAttribute('sort-direction') || activeSortFromUrl() || 'created-descending';
  }

  get isExplicit() {
    try {
      return sessionStorage.getItem(EXPLICIT_STORAGE_KEY) === '1';
    } catch (_error) {
      return false;
    }
  }

  get productList() {
    return this.querySelector('product-list');
  }

  get paginationEl() {
    return this.querySelector('.search-date-sort__pagination');
  }

  get progressText() {
    return this.querySelector('.search-date-sort__progress-text');
  }

  get slowNotice() {
    return this.querySelector('[data-date-sort-slow]');
  }

  #clearTimers() {
    if (this.#timeoutId) {
      clearTimeout(this.#timeoutId);
      this.#timeoutId = null;
    }
    if (this.#modalTimeoutId) {
      clearTimeout(this.#modalTimeoutId);
      this.#modalTimeoutId = null;
    }
    if (this.#idleTimeoutId) {
      clearTimeout(this.#idleTimeoutId);
      this.#idleTimeoutId = null;
    }
  }

  #bumpProgress(message) {
    if (message) this.#setProgress(message);
    if (this.#idleTimeoutId) clearTimeout(this.#idleTimeoutId);
    const idleMs = this.isExplicit ? Math.min(20000, EXPLICIT_TIMEOUT_MS) : DEFAULT_IDLE_MS;
    this.#idleTimeoutId = setTimeout(() => {
      if (!this.#applied) this.#fallbackToRelevance();
    }, idleMs);
  }

  #showSlowNotice() {
    const notice = this.slowNotice;
    if (!notice) return;
    notice.hidden = false;
    notice.classList.add('is-visible');
  }

  #hideSlowNotice() {
    const notice = this.slowNotice;
    if (!notice) return;
    notice.hidden = true;
    notice.classList.remove('is-visible');
  }

  #releaseScrollLock({ closeDialogs = true } = {}) {
    if (closeDialogs) {
      this.#hideSlowNotice();

      document.querySelectorAll('x-modal[id^="search-date-sort-modal-"]').forEach((el) => {
        try {
          el.hide?.();
          el.removeAttribute('open');
          el.remove();
        } catch (_error) {
          // ignore
        }
      });

      document.querySelectorAll('header-search[open]').forEach((el) => {
        try {
          el.hide?.();
          el.removeAttribute('open');
        } catch (_error) {
          el.removeAttribute('open');
        }
      });
    }

    const root = document.documentElement;
    root.classList.remove('lock');
    root.style.removeProperty('overflow');
    root.style.removeProperty('overflow-y');
    root.style.removeProperty('height');
    root.style.removeProperty('max-height');

    if (document.body) {
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('height');
      document.body.style.removeProperty('max-height');
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('width');
    }

    document.querySelectorAll('.main-search__tabs, x-tabs.main-search__tabs').forEach((tabs) => {
      tabs.style.removeProperty('height');
      tabs.style.removeProperty('max-height');
      tabs.style.removeProperty('overflow');
    });
  }

  #bindModalActions() {
    if (this.#modalBound) return;
    this.#modalBound = true;

    const waitBtn = this.querySelector('[data-date-sort-wait]');
    const relevanceBtn = this.querySelector('[data-date-sort-relevance]');

    waitBtn?.addEventListener('click', () => {
      this.#waitExtended = true;
      this.#hideSlowNotice();
      this.#clearTimers();
      const remaining = Math.max(1000, EXPLICIT_TIMEOUT_MS - (Date.now() - this.#startedAt));
      this.#timeoutId = setTimeout(() => {
        if (!this.#applied) this.#fallbackToRelevance();
      }, remaining);
    });

    relevanceBtn?.addEventListener('click', () => {
      this.#hideSlowNotice();
      this.#fallbackToRelevance();
    });
  }

  #bindPagination() {
    if (this.#paginationBound) return;
    this.#paginationBound = true;

    this.paginationEl?.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-date-page]');
      if (!link) return;
      event.preventDefault();
      event.stopPropagation();

      const page = Number(link.getAttribute('data-date-page'));
      if (!Number.isFinite(page) || page < 1) return;

      const url = new URL(window.location.href);
      if (page <= 1) {
        url.searchParams.delete('page');
      } else {
        url.searchParams.set('page', String(page));
      }
      history.replaceState({}, '', url.toString());

      this.#renderPage(page).catch((error) => {
        if (error?.name !== 'AbortError') {
          this.#fallbackToRelevance();
        }
      });
    });
  }

  #setProgress(message) {
    if (this.progressText && message) {
      this.progressText.textContent = message;
    }
  }

  #startTimeouts() {
    this.#clearTimers();
    this.#waitExtended = false;

    if (this.isExplicit) {
      this.#modalTimeoutId = setTimeout(() => {
        if (this.#applied || this.#waitExtended) return;
        this.#showSlowNotice();
      }, EXPLICIT_MODAL_AT_MS);

      this.#timeoutId = setTimeout(() => {
        if (!this.#applied && !this.#waitExtended) {
          this.#fallbackToRelevance();
        }
      }, EXPLICIT_TIMEOUT_MS);
    } else {
      this.#timeoutId = setTimeout(() => {
        if (!this.#applied) this.#fallbackToRelevance();
      }, DEFAULT_TIMEOUT_MS);
      this.#bumpProgress();
    }
  }

  async #run(generation) {
    this.#startTimeouts();

    const products = await this.#loadMeta(generation);
    if (generation !== this.#generation) return;

    this.#sortedProducts = this.#sortProducts(products, this.sortDirection);
    const url = new URL(window.location.href);
    const page = Math.max(1, Number(url.searchParams.get('page') || '1'));

    await this.#renderPage(page, generation);
    if (generation !== this.#generation) return;

    this.#applied = true;
    this.#clearTimers();
    this.#releaseScrollLock();
    setPending(false);
    this.removeAttribute('aria-busy');
    this.productList?.removeAttribute('aria-busy');

    const progress = this.querySelector('.search-date-sort__progress');
    if (progress) progress.hidden = true;

    const serverPagination = this.querySelector('.search-date-server-pagination');
    if (serverPagination) serverPagination.hidden = true;
  }

  async #loadMeta(generation) {
    const key = metaCacheKey();
    if (metaCache?.key === key) {
      return metaCache.products.slice();
    }

    const signal = this.#abortController.signal;
    const firstResponse = await fetch(buildMetaUrl(1).toString(), { signal });
    if (!firstResponse.ok) throw new Error('Meta page 1 failed');

    const firstPayload = extractJson(await firstResponse.text());
    if (generation !== this.#generation) throw new DOMException('Aborted', 'AbortError');

    const resultsCount = Number(firstPayload.results_count) || 0;
    const pages = Number(firstPayload.pages) || 1;

    if (resultsCount > MAX_PRODUCTS || pages > MAX_META_PAGES) {
      throw new Error('Match set exceeds date-sort cap');
    }

    let products = Array.isArray(firstPayload.products) ? firstPayload.products.slice() : [];
    this.#bumpProgress(this.#loadingLabel(1, pages));

    if (pages > 1) {
      const pageNumbers = [];
      for (let page = 2; page <= pages; page += 1) pageNumbers.push(page);

      const rest = await mapPool(
        pageNumbers,
        META_CONCURRENCY,
        async (page, index) => {
          const response = await fetch(buildMetaUrl(page).toString(), { signal });
          if (!response.ok) throw new Error(`Meta page ${page} failed`);
          const payload = extractJson(await response.text());
          this.#bumpProgress(this.#loadingLabel(index + 2, pages));
          await yieldToMain();
          return Array.isArray(payload.products) ? payload.products : [];
        },
        signal
      );

      for (const batch of rest) {
        products = products.concat(batch);
      }
    }

    if (products.length > MAX_PRODUCTS) {
      throw new Error('Product list exceeds cap');
    }

    metaCache = { key, products: products.slice() };
    return products;
  }

  #loadingLabel(current, total) {
    const template = this.getAttribute('data-loading-template') || 'Loading {{ current }} of {{ total }}…';
    return template.replace('{{ current }}', String(current)).replace('{{ total }}', String(total));
  }

  #sortProducts(products, direction) {
    const sorted = products.slice();
    sorted.sort((a, b) => {
      const dateA = Number(a.created_at) || 0;
      const dateB = Number(b.created_at) || 0;
      return direction === 'created-ascending' ? dateA - dateB : dateB - dateA;
    });
    return sorted;
  }

  async #renderPage(page, generation = this.#generation) {
    const perPage = this.productsPerPage;
    const total = this.#sortedProducts.length;
    const totalPages = Math.max(1, Math.ceil(total / perPage) || 1);
    const safePage = Math.min(Math.max(1, page), totalPages);
    const start = (safePage - 1) * perPage;
    const slice = this.#sortedProducts.slice(start, start + perPage);

    this.#bumpProgress(
      this.getAttribute('data-loading-products') ||
        this.progressText?.textContent ||
        'Loading products…'
    );

    const nodes = await this.#fetchCards(
      slice.map((item) => item.handle),
      generation
    );
    if (generation !== this.#generation) return;

    if (slice.length > 0 && nodes.length === 0) {
      throw new Error('No product cards returned');
    }

    const list = this.productList;
    if (!list) return;

    const fragment = document.createDocumentFragment();
    nodes.forEach((node) => {
      if (node) fragment.appendChild(node.cloneNode(true));
    });

    list.replaceChildren(fragment);
    this.#rebuildPagination(safePage, totalPages);
    this.#releaseScrollLock({ closeDialogs: false });
  }

  async #fetchCards(handles, generation) {
    const signal = this.#abortController.signal;
    const unique = handles.filter(Boolean);
    if (unique.length === 0) return [];

    await mapPool(
      unique,
      CARD_CONCURRENCY,
      async (handle) => {
        if (cardCache.has(handle)) return cardCache.get(handle);
        try {
          const response = await fetch(buildCardUrl(handle), { signal });
          if (!response.ok) return null;
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const card = doc.querySelector('product-card');
          if (card) cardCache.set(handle, card);
          this.#bumpProgress();
          await yieldToMain();
          return card;
        } catch (error) {
          if (error?.name === 'AbortError') throw error;
          return null;
        }
      },
      signal
    );

    if (generation !== this.#generation) throw new DOMException('Aborted', 'AbortError');

    return unique.map((handle) => cardCache.get(handle) || null).filter(Boolean);
  }

  #chevronSvg(direction) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('width', '11');
    svg.setAttribute('viewBox', '0 0 10 10');
    svg.setAttribute(
      'class',
      direction === 'left'
        ? 'icon icon-chevron-left icon--direction-aware'
        : 'icon icon-chevron-right icon--direction-aware'
    );
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', direction === 'left' ? 'M7 1 3 5l4 4' : 'm3 9 4-4-4-4');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-linecap', 'square');
    svg.appendChild(path);
    return svg;
  }

  #rebuildPagination(currentPage, totalPages) {
    const nav = this.paginationEl;
    if (!nav) return;

    if (totalPages <= 1) {
      nav.hidden = true;
      nav.replaceChildren();
      return;
    }

    nav.hidden = false;
    nav.removeAttribute('hidden');
    const parts = [];

    const pushLink = (page, content, rel) => {
      const a = document.createElement('a');
      a.className = 'pagination__link h6';
      a.href = '#';
      a.setAttribute('data-date-page', String(page));
      if (rel) a.rel = rel;
      a.setAttribute('aria-label', `Go to page ${page}`);
      if (typeof content === 'string') {
        a.textContent = content;
      } else {
        a.appendChild(content);
      }
      parts.push(a);
    };

    if (currentPage > 1) {
      pushLink(currentPage - 1, this.#chevronSvg('left'), 'prev');
    }

    for (let page = 1; page <= totalPages; page += 1) {
      if (totalPages > 7) {
        const nearStart = page <= 2;
        const nearEnd = page > totalPages - 2;
        const nearCurrent = Math.abs(page - currentPage) <= 1;
        if (!nearStart && !nearEnd && !nearCurrent) {
          if (page === 3 && currentPage > 4) {
            const span = document.createElement('span');
            span.className = 'pagination__link pagination__link--disabled h6';
            span.textContent = '…';
            parts.push(span);
          }
          if (page === totalPages - 2 && currentPage < totalPages - 3) {
            const span = document.createElement('span');
            span.className = 'pagination__link pagination__link--disabled h6';
            span.textContent = '…';
            parts.push(span);
          }
          continue;
        }
      }

      if (page === currentPage) {
        const span = document.createElement('span');
        span.className = 'pagination__link pagination__link--disabled h6';
        span.setAttribute('aria-current', 'page');
        span.textContent = String(page);
        parts.push(span);
      } else {
        pushLink(page, String(page));
      }
    }

    if (currentPage < totalPages) {
      pushLink(currentPage + 1, this.#chevronSvg('right'), 'next');
    }

    nav.replaceChildren(...parts);
  }

  async #fallbackToRelevance() {
    this.#clearTimers();
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#applied = false;
    this.#releaseScrollLock();
    setPending(false);

    try {
      sessionStorage.removeItem(EXPLICIT_STORAGE_KEY);
    } catch (_error) {
      // ignore
    }

    const url = new URL(window.location.href);
    url.searchParams.delete('sort_by');
    url.searchParams.delete(THEME_SORT_PARAM);
    url.searchParams.delete('section_id');
    history.replaceState({}, '', url.toString());

    window.__SEARCH_DATE_SORT__ = null;
    syncSortDropdown('relevance');
    syncSortHiddenInputs('relevance');

    const sectionUrl = new URL(window.location.href);
    sectionUrl.searchParams.delete(THEME_SORT_PARAM);
    sectionUrl.searchParams.set('sort_by', 'relevance');
    sectionUrl.searchParams.set('section_id', this.sectionId);

    try {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
      const response = await fetch(sectionUrl.toString(), { signal: this.#abortController.signal });
      const html = await response.text();
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newSection = doc.querySelector('.shopify-section');
      const currentSection = document.getElementById(`shopify-section-${this.sectionId}`);
      if (newSection && currentSection) {
        currentSection.replaceChildren(...document.importNode(newSection, true).childNodes);
      }

      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('sort_by');
      cleanUrl.searchParams.delete(THEME_SORT_PARAM);
      history.replaceState({}, '', cleanUrl.toString());
      syncSortDropdown('relevance');
      setPending(false);
    } catch (error) {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      if (error?.name !== 'AbortError') {
        window.location.href = url.toString();
      }
    }
  }
}

if (!window.customElements.get('search-date-sort')) {
  window.customElements.define('search-date-sort', SearchDateSort);
}

if (!window.__SEARCH_DATE_SORT_LISTENERS__) {
  window.__SEARCH_DATE_SORT_LISTENERS__ = true;

  let lastFacetUpdateKey = '';
  let lastFacetUpdateAt = 0;

  document.addEventListener(
    'listbox:change',
    (event) => {
      const value = event.detail?.value;
      const popover = event.target?.closest?.('facets-sort-popover');
      if (!popover || value == null) return;
      if (!isSearchResultsPage()) return;

      const next = normalizeSearchSort(value);
      const current = activeSortFromUrl();

      if (next === current) {
        event.stopImmediatePropagation();
        if (isDateSortValue(next)) {
          window.__SEARCH_DATE_SORT__ = next;
        } else {
          window.__SEARCH_DATE_SORT__ = null;
          setPending(false);
        }
        return;
      }

      if (!isDateSortValue(value)) {
        window.__SEARCH_DATE_SORT__ = null;
        try {
          sessionStorage.removeItem(EXPLICIT_STORAGE_KEY);
        } catch (_error) {
          // ignore
        }
        setPending(false);
        return;
      }

      window.__SEARCH_DATE_SORT__ = value;
      try {
        sessionStorage.setItem(EXPLICIT_STORAGE_KEY, '1');
      } catch (_error) {
        // ignore
      }
    },
    true
  );

  document.addEventListener(
    'facet:update',
    (event) => {
      const url = event.detail?.url;
      if (!(url instanceof URL)) return;

      if (isSearchResultsPage() || url.pathname.includes('/search')) {
        rewriteFacetUrl(url);
      }

      const params = new URLSearchParams(url.searchParams);
      params.delete('section_id');
      const key = `${url.pathname}?${params.toString()}`;
      const now = Date.now();

      if (key === lastFacetUpdateKey && now - lastFacetUpdateAt < 2000) {
        event.stopImmediatePropagation();
        return;
      }

      lastFacetUpdateKey = key;
      lastFacetUpdateAt = now;
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const dateSort = isDateSortValue(urlThemeSort())
        ? urlThemeSort()
        : isDateSortValue(urlSortBy())
          ? urlSortBy()
          : null;

      if (isNewQuerySearchForm(form)) {
        const themeInput = form.querySelector('input[name="theme_sort"]');
        if (themeInput) {
          themeInput.disabled = false;
          themeInput.value = 'created-descending';
        }
        const sortInput = form.querySelector('input[name="sort_by"]');
        if (sortInput) {
          sortInput.disabled = true;
          sortInput.value = '';
        }
        try {
          sessionStorage.removeItem(EXPLICIT_STORAGE_KEY);
        } catch (_error) {
          // ignore
        }
        metaCache = null;
        return;
      }

      if (dateSort) {
        const themeInput = form.querySelector('input[name="theme_sort"]');
        if (themeInput) {
          themeInput.disabled = false;
          themeInput.value = dateSort;
        }
        const sortInput = form.querySelector('input[name="sort_by"]');
        if (sortInput) {
          sortInput.disabled = true;
          sortInput.value = '';
        }
      }
    },
    true
  );
}
